<template>
  <azcon-slider-skeleton v-if="loading"/>

  <section v-else class="hero">
    <div
      class="hero-slider"
      @touchstart.passive="onTouchStart"
      @touchend="onTouchEnd"
    >
      <article
        v-for="(item, idx) in advertisements"
        :key="'slide_' + idx"
        class="slide slide-welcome"
        :class="{ 'is-active': currentSlide === idx }"
      >
        <img
          v-if="item.image_link && shouldRenderImage(idx)"
          class="slide-bg"
          :src="item.image_link"
          :alt="item.name || ''"
          :loading="isActiveSlide(idx) ? 'eager' : 'lazy'"
          decoding="async"
          :fetchpriority="isActiveSlide(idx) ? 'high' : 'low'"
        />
        <div v-if="item.name || item.text || item.button_text" class="slide-content">
          <h2 v-if="item.name" class="slide-content__title">{{ item.name }}</h2>
          <p v-if="item.text" class="slide-content__text">{{ item.text }}</p>
          <a v-if="item.url || item.button_text"
             :href="item.url"
             :target="item.is_new_tab ? '_blank' : '_self'"
             class="slide-content__btn"
          >
            {{ item.button_text }}
          </a>
        </div>
      </article>

      <!-- Controls -->
      <div v-if="advertisements.length > 1" class="slider-controls">
        <button
          class="slider-arrow"
          type="button"
          @click="prev"
        >&#8249;</button>
        <div class="slider-dots">
          <button
            v-for="(item, idx) in advertisements"
            :key="'dot_' + idx"
            class="dot"
            :class="{ 'is-active': currentSlide === idx }"
            type="button"
            @click="goTo(idx)"
          ></button>
        </div>
        <button
          class="slider-arrow"
          type="button"
          @click="next"
        >&#8250;</button>
      </div>
    </div>
  </section>
</template>

<script>
import AzconSliderSkeleton from "./AzconSliderSkeleton.vue";

export default {
  name: "AzconSlider",
  components: { AzconSliderSkeleton },
  props: {
    advertisements: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentSlide: 0,
      timer: null,
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  watch: {
    advertisements() {
      this.currentSlide = 0;
      this.restartAutoplay();
      this.preloadNearbyImages();
    },
    currentSlide() {
      this.preloadNearbyImages();
    },
  },
  methods: {
    isActiveSlide(idx) {
      return this.currentSlide === idx;
    },

    shouldRenderImage(idx) {
      if (!this.advertisements.length) return false;
      if (this.isActiveSlide(idx)) return true;
      const next = (this.currentSlide + 1) % this.advertisements.length;
      const prev = (this.currentSlide - 1 + this.advertisements.length) % this.advertisements.length;
      return idx === next || idx === prev;
    },

    preloadImage(src) {
      if (!src) return;
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    },

    preloadNearbyImages() {
      if (!this.advertisements.length) return;
      const current = this.advertisements[this.currentSlide];
      const next = this.advertisements[(this.currentSlide + 1) % this.advertisements.length];
      const prev = this.advertisements[(this.currentSlide - 1 + this.advertisements.length) % this.advertisements.length];

      this.preloadImage(current?.image_link);
      this.preloadImage(next?.image_link);
      this.preloadImage(prev?.image_link);
    },

    prev() {
      this.currentSlide = (this.currentSlide - 1 + this.advertisements.length) % this.advertisements.length;
      this.restartAutoplay();
    },
    next() {
      this.currentSlide = (this.currentSlide + 1) % this.advertisements.length;
      this.restartAutoplay();
    },
    goTo(idx) {
      this.currentSlide = idx;
      this.restartAutoplay();
    },
    onTouchStart(event) {
      const touch = event.touches && event.touches[0];
      if (!touch) return;

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    },
    onTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      const swipeThreshold = 40;

      this.touchStartX = 0;
      this.touchStartY = 0;

      if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (deltaX < 0) {
        this.next();
      } else {
        this.prev();
      }
    },
    startAutoplay() {
      if (this.advertisements.length <= 1) return;
      this.timer = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.advertisements.length;
      }, 10000);
    },
    restartAutoplay() {
      clearInterval(this.timer);
      this.startAutoplay();
    },
  },
  mounted() {
    this.startAutoplay();
    this.preloadNearbyImages();
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>

<style scoped>
.hero {
  margin-top: 10px;
  border-radius: 12px;
  padding: 0;
  background: linear-gradient(120deg, #273548 0%, #516c89 60%, #7993ae 100%);
  color: #fff;
  overflow: hidden;
}

.hero-slider {
  position: relative;
  min-height: 0;
  aspect-ratio: 2048 / 517;
  width: 100%;
  height: 100%;
}

.slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transform: translateX(12px);
  transition: opacity 0.35s ease, transform 0.35s ease;
  pointer-events: none;
}

.slide.is-active {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.slide-welcome {
  border-radius: 12px;
  overflow: hidden;
  background: #0f1d30;
}

.slide-welcome::before {
  content: none;
}

.slide-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  z-index: 0;
}

.slide-content {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  padding: 2rem 3rem;
  max-width: 55%;
  z-index: 2;
}

.slide-content__title {
  font-size: clamp(1.1rem, 2.5vw, 2rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem;
  text-shadow: 1px 2px 6px rgba(0, 0, 0, 0.45);
  line-height: 1.25;
}

.slide-content__text {
  font-size: clamp(0.8rem, 1.3vw, 1rem);
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.4);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.slide-content__btn {
  display: inline-block;
  padding: 0.55rem 1.25rem;
  background: rgba(0, 51, 102, 0.85);
  color: #fff;
  text-decoration: none;
  border-radius: 8px;
  font-size: clamp(0.8rem, 1.1vw, 0.95rem);
  font-weight: 600;
  border: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.slide-content__btn:hover {
  background: rgba(0, 51, 102, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
}

.slide-content__btn:focus,
.slide-content__btn:active {
  outline: none;
  box-shadow: none;
}

.slider-controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 3;
}

.slider-arrow {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font: inherit;
  font-size: 20px;
  line-height: 1;
  display: grid;
  place-items: center;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.slider-dots {
  display: flex;
  align-items: center;
  gap: 7px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.45);
  padding: 0;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: width 0.2s ease, border-radius 0.2s ease, background 0.2s ease;
}

.dot.is-active {
  width: 18px;
  border-radius: 999px;
  background: #fff;
}

/* ---- Tablet (768px - 1024px) ---- */
@media (max-width: 1024px) {
  .slide-content {
    max-width: 65%;
    padding: 1.5rem 2rem;
  }

  .slide-content__text {
    -webkit-line-clamp: 2;
    margin-bottom: 0.75rem;
  }
}

/* ---- Mobile Landscape (576px - 768px) ---- */
@media (max-width: 768px) {
  .hero,
  .slide-welcome {
    border-radius: 0;
  }

  .hero-slider {
    aspect-ratio: 16 / 7;
  }

  .slide-content {
    max-width: 70%;
    padding: 1rem 1.25rem;
  }

  .slide-content__title {
    font-size: 1rem;
    margin-bottom: 0.3rem;
  }

  .slide-content__text {
    font-size: 0.8rem;
    -webkit-line-clamp: 2;
    margin-bottom: 0.5rem;
  }

  .slide-content__btn {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    min-height: 36px;
  }

  .slider-controls {
    bottom: 6px;
    gap: 8px;
  }

  .slider-arrow {
    display: none;
  }
}

/* ---- Mobile Portrait (< 576px) ---- */
@media (max-width: 576px) {
  .hero {
    border-radius: 0;
  }

  .hero-slider {
    aspect-ratio: 16 / 9;
  }

  .slide-welcome {
    border-radius: 0;
  }

  .slide-content {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    max-width: 100%;
    padding: 2.5rem 1rem 1rem;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, transparent 100%);
  }

  .slide-content__title {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }

  .slide-content__text {
    font-size: 0.875rem;
    -webkit-line-clamp: 2;
    margin-bottom: 0.5rem;
  }

  .slide-content__btn {
    padding: 0.5rem 1rem;
    min-height: 48px;
    font-size: 0.9375rem;
    border-radius: 6px;
    min-height: 40px;
  }

  .slider-controls {
    bottom: 4px;
    gap: 6px;
  }

  .dot {
    width: 6px;
    height: 6px;
  }

  .dot.is-active {
    width: 14px;
  }
}

/* ---- Extra Small (< 400px) ---- */
@media (max-width: 400px) {
  .hero-slider {
    aspect-ratio: 16 / 10;
  }

  .slide-content {
    padding: 2rem 0.75rem 0.75rem;
  }

  .slide-content__title {
    font-size: 1rem;
  }

  .slide-content__text {
    font-size: 0.875rem;
    -webkit-line-clamp: 2;
  }

  .slide-content__btn {
    padding: 0.5rem 1rem;
    font-size: 0.9375rem;
    min-height: 48px;
  }
}
</style>
